import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import GoogleMapReact from "google-map-react";
import { applyGradualMapZoom, withGradualZoomOptions } from "../../utils/gradualMapZoom";
import MDSpinner from "react-md-spinner";

import myConfig from "../../configs/config";
import {
  applyViewportToGoogleMap,
  normalizeCenter,
  resolveMapViewport,
} from "../../utils/mapViewport";
import {
  fetchFarmMapContext,
  saveWeatherReference,
} from "../../utils/weatherReferenceApi";

import "./FarmClimateReferenceModal.css";

function formatCoord(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return "";
  return String(Math.round(number * 1e6) / 1e6);
}

function parseCoord(value) {
  if (value == null || String(value).trim() === "") return null;
  const number = Number(String(value).replace(",", "."));
  return Number.isFinite(number) ? number : null;
}

function FarmClimateReferenceModal({
  farmId,
  initialLat,
  initialLng,
  onClose,
  onSaved,
}) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [latInput, setLatInput] = useState(
    initialLat != null ? formatCoord(initialLat) : ""
  );
  const [lngInput, setLngInput] = useState(
    initialLng != null ? formatCoord(initialLng) : ""
  );
  const [mapViewport, setMapViewport] = useState(null);

  const mapRef = useRef(null);
  const mapsRef = useRef(null);
  const markerRef = useRef(null);

  const selectedLat = parseCoord(latInput);
  const selectedLng = parseCoord(lngInput);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchFarmMapContext(farmId)
      .then((farm) => {
        if (cancelled) return;
        const savedPoint =
          initialLat != null && initialLng != null
            ? { lat: Number(initialLat), lng: Number(initialLng) }
            : normalizeCenter({
                lat: farm && farm.weather_reference && farm.weather_reference.lat,
                lng: farm && farm.weather_reference && farm.weather_reference.lng,
              });
        const viewport = resolveMapViewport({
          apiViewport: farm && farm.map_viewport,
          localViewport: null,
          fallbackCenter: savedPoint || (farm && farm.farm_center),
          fallbackZoom: savedPoint ? 17 : 16,
          preferApi: true,
        });
        if (savedPoint) {
          setLatInput(formatCoord(savedPoint.lat));
          setLngInput(formatCoord(savedPoint.lng));
        }
        setMapViewport(viewport);
      })
      .catch(() => {
        if (!cancelled) {
          setMapViewport(
            resolveMapViewport({
              fallbackCenter:
                initialLat != null && initialLng != null
                  ? { lat: Number(initialLat), lng: Number(initialLng) }
                  : null,
              fallbackZoom: 16,
            })
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [farmId, initialLat, initialLng]);

  const mapCenter = useMemo(() => {
    if (selectedLat != null && selectedLng != null) {
      return { lat: selectedLat, lng: selectedLng };
    }
    if (mapViewport) {
      return { lat: mapViewport.lat, lng: mapViewport.lng };
    }
    return { lat: -15.78, lng: -47.93 };
  }, [selectedLat, selectedLng, mapViewport]);

  function placeMarker(lat, lng) {
    const maps = mapsRef.current;
    const map = mapRef.current;
    if (!maps || !map || lat == null || lng == null) return;
    const position = { lat, lng };
    if (markerRef.current) {
      markerRef.current.setPosition(position);
      return;
    }
    markerRef.current = new maps.Marker({
      position,
      map,
      draggable: true,
      title: t("Ponto de referência do clima"),
    });
    maps.event.addListener(markerRef.current, "dragend", function () {
      const next = markerRef.current.getPosition();
      if (!next) return;
      setLatInput(formatCoord(next.lat()));
      setLngInput(formatCoord(next.lng()));
    });
  }

  useEffect(() => {
    if (selectedLat != null && selectedLng != null) {
      placeMarker(selectedLat, selectedLng);
    }
  }, [selectedLat, selectedLng]);

  function handleApiLoaded(google) {
    mapRef.current = google.map;
    applyGradualMapZoom(google.map);
    mapsRef.current = google.maps;
    if (mapViewport) {
      applyViewportToGoogleMap(google.map, mapViewport);
    }
    if (selectedLat != null && selectedLng != null) {
      placeMarker(selectedLat, selectedLng);
    }
  }

  function handleMapClick({ lat, lng }) {
    setError("");
    setLatInput(formatCoord(lat));
    setLngInput(formatCoord(lng));
    placeMarker(lat, lng);
  }

  function useMapCenter() {
    const map = mapRef.current;
    if (!map || !map.getCenter) return;
    const center = map.getCenter();
    handleMapClick({ lat: center.lat(), lng: center.lng() });
  }

  async function handleSubmit(event) {
    event.preventDefault();
    const lat = parseCoord(latInput);
    const lng = parseCoord(lngInput);
    if (lat == null || lng == null) {
      setError(t("Informe latitude e longitude válidas."));
      return;
    }
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      setError(t("Latitude e longitude fora do intervalo válido."));
      return;
    }
    setSaving(true);
    setError("");
    try {
      const saved = await saveWeatherReference(farmId, { lat, lng });
      onSaved(saved);
    } catch (submitError) {
      const detail =
        submitError &&
        submitError.response &&
        submitError.response.data &&
        submitError.response.data.detail;
      setError(detail || t("Não foi possível salvar o ponto de referência."));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="farm-climate-ref-modal">
      <button
        type="button"
        className="farm-climate-ref-modal__backdrop"
        aria-label={t("Fechar")}
        onClick={onClose}
      />
      <form className="farm-climate-ref-modal__panel" onSubmit={handleSubmit}>
        <h3>{t("Definir ponto de referência do clima")}</h3>
        <p className="farm-climate-ref-modal__hint">
          {t(
            "Informe latitude e longitude ou clique no mapa satélite. O mapa abre na fazenda. Use o centro da tela ou um clique para marcar a origem dos dados de clima."
          )}
        </p>

        <div className="farm-climate-ref-modal__coords">
          <label>
            {t("Latitude")}
            <input
              type="text"
              inputMode="decimal"
              value={latInput}
              onChange={(event) => setLatInput(event.target.value)}
              placeholder="-23.550500"
            />
          </label>
          <label>
            {t("Longitude")}
            <input
              type="text"
              inputMode="decimal"
              value={lngInput}
              onChange={(event) => setLngInput(event.target.value)}
              placeholder="-51.461400"
            />
          </label>
        </div>

        <div className="farm-climate-ref-modal__map-wrap">
          {loading || !mapViewport ? (
            <div className="farm-climate-ref-modal__map-loading">
              <MDSpinner singleColor="#1ABB9C" size={36} />
            </div>
          ) : (
            <GoogleMapReact
              bootstrapURLKeys={{ key: myConfig.GOOGLE_API_KEY }}
              defaultCenter={mapCenter}
              defaultZoom={mapViewport.zoom || 16}
              options={(maps) =>
                withGradualZoomOptions({
                  streetViewControl: false,
                  scaleControl: true,
                  fullscreenControl: true,
                  gestureHandling: "greedy",
                  mapTypeId: maps.MapTypeId.SATELLITE,
                  clickableIcons: false,
                })
              }
              yesIWantToUseGoogleMapApiInternals
              onGoogleApiLoaded={handleApiLoaded}
              onClick={handleMapClick}
            />
          )}
          <div className="farm-climate-ref-modal__crosshair" aria-hidden="true" />
        </div>

        <div className="farm-climate-ref-modal__map-actions">
          <button
            type="button"
            className="btn btn-default btn-sm"
            onClick={useMapCenter}
            disabled={loading}
          >
            {t("Usar o centro da tela")}
          </button>
        </div>

        {error ? (
          <div className="farm-climate-ref-modal__error">{error}</div>
        ) : null}

        <div className="farm-climate-ref-modal__actions">
          <button
            type="button"
            className="btn btn-default"
            onClick={onClose}
            disabled={saving}
          >
            {t("Cancelar")}
          </button>
          <button type="submit" className="btn btn-success" disabled={saving}>
            {saving ? t("Salvando...") : t("Salvar ponto")}
          </button>
        </div>
      </form>
    </div>
  );
}

export default FarmClimateReferenceModal;
